import React, { useEffect, useRef } from "react";
import {
  Map,
  AdvancedMarker,
  InfoWindow,
  useMap,
  Pin,
  ControlPosition,
} from "@vis.gl/react-google-maps";
import NearMe from "@mui/icons-material/NearMe";
import { Fab, Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { PhlebotomyLocation } from "../types";
import "./PhlebotomyLocationFinderMap.css";

const DEFAULT_MAP_CENTER = { lat: 37.5, lng: -122.2 };
const DEFAULT_MAP_ZOOM = 9;
const SELECTED_LOCATION_TOP_PADDING_PX = 72;
const MAP_VIEWPORT_PADDING_PX = 16;
const WEB_MERCATOR_TILE_SIZE_PX = 256;
const MAX_WEB_MERCATOR_LATITUDE = 85.05112878;

type MappableLocation = PhlebotomyLocation & {
  coordinates: { lat: number; lng: number };
};

interface MapViewportInset {
  bottomInsetRatio: number;
  bottomInsetPixels: number;
}

const hasCoordinates = (
  location: PhlebotomyLocation,
): location is MappableLocation => location.coordinates !== null;

const getPaddedSelectionCenter = (
  coordinates: { lat: number; lng: number },
  zoom: number | undefined,
  bottomInsetPixels = 0,
  topPaddingPixels = SELECTED_LOCATION_TOP_PADDING_PX,
) => {
  if (zoom == null) return coordinates;

  const boundedLatitude = Math.max(
    -MAX_WEB_MERCATOR_LATITUDE,
    Math.min(MAX_WEB_MERCATOR_LATITUDE, coordinates.lat),
  );
  const latitudeRadians = (boundedLatitude * Math.PI) / 180;
  const normalizedWorldY =
    0.5 -
    Math.log(
      (1 + Math.sin(latitudeRadians)) / (1 - Math.sin(latitudeRadians)),
    ) /
      (4 * Math.PI);
  const normalizedPaddedWorldY =
    normalizedWorldY +
    (bottomInsetPixels - topPaddingPixels) /
      (WEB_MERCATOR_TILE_SIZE_PX * 2 ** zoom);
  const paddedLatitudeRadians = Math.atan(
    Math.sinh(Math.PI * (1 - 2 * normalizedPaddedWorldY)),
  );

  return {
    lat: (paddedLatitudeRadians * 180) / Math.PI,
    lng: coordinates.lng,
  };
};

const getBottomInsetPixels = (
  map: google.maps.Map,
  bottomInsetRatio: number,
  bottomInsetPixels: number,
) =>
  bottomInsetPixels +
  (bottomInsetRatio > 0 ? map.getDiv().clientHeight * bottomInsetRatio : 0);

const getMapViewportPadding = (
  map: google.maps.Map,
  bottomInsetRatio: number,
  bottomInsetPixels: number,
) => ({
  top: MAP_VIEWPORT_PADDING_PX,
  right: MAP_VIEWPORT_PADDING_PX,
  bottom:
    MAP_VIEWPORT_PADDING_PX +
    getBottomInsetPixels(map, bottomInsetRatio, bottomInsetPixels),
  left: MAP_VIEWPORT_PADDING_PX,
});

const useMapAutoCenter = (
  map: google.maps.Map | null,
  locations: PhlebotomyLocation[],
  userLocation: { lat: number; lng: number } | null,
  emptyLocationsZoom: number,
  preferNearbyRadiusMiles: number | undefined,
  viewportInsetRef: React.MutableRefObject<MapViewportInset>,
) => {
  useEffect(() => {
    if (!map) return;
    const { bottomInsetRatio, bottomInsetPixels } = viewportInsetRef.current;
    const mappableLocations = locations.filter(hasCoordinates);
    if (mappableLocations.length === 0) {
      if (userLocation) {
        map.setZoom(emptyLocationsZoom);
        map.setCenter(
          getPaddedSelectionCenter(
            userLocation,
            emptyLocationsZoom,
            getBottomInsetPixels(map, bottomInsetRatio, bottomInsetPixels) / 2,
            0,
          ),
        );
      }
      return;
    }

    let framingLocations = mappableLocations;
    if (preferNearbyRadiusMiles != null) {
      const nearbyLocations = mappableLocations.filter(
        (location) =>
          location.distanceMiles != null &&
          location.distanceMiles <= preferNearbyRadiusMiles,
      );
      // Prefer a tight nearby frame; otherwise zoom out just enough for the
      // nearest few results. Catalog order is type/preference first, not distance.
      framingLocations =
        nearbyLocations.length > 0
          ? nearbyLocations
          : [...mappableLocations]
              .sort((left, right) => {
                const leftDistance =
                  left.distanceMiles ?? Number.POSITIVE_INFINITY;
                const rightDistance =
                  right.distanceMiles ?? Number.POSITIVE_INFINITY;
                return leftDistance - rightDistance;
              })
              .slice(0, 3);
    }

    // Handle single location case with reasonable zoom level
    if (framingLocations.length === 1 && !userLocation) {
      map.setZoom(18);
      map.setCenter(
        getPaddedSelectionCenter(
          framingLocations[0].coordinates,
          18,
          getBottomInsetPixels(map, bottomInsetRatio, bottomInsetPixels) / 2,
          0,
        ),
      );
      return;
    }

    // Fit framing locations and the optional search origin.
    const bounds = new google.maps.LatLngBounds();
    framingLocations.forEach((location) => bounds.extend(location.coordinates));
    if (userLocation) {
      bounds.extend(userLocation);
    }
    const viewportBottomInsetPixels = getBottomInsetPixels(
      map,
      bottomInsetRatio,
      bottomInsetPixels,
    );
    if (viewportBottomInsetPixels > 0) {
      map.fitBounds(
        bounds,
        getMapViewportPadding(map, bottomInsetRatio, bottomInsetPixels),
      );
    } else {
      map.fitBounds(bounds);
    }

    // Avoid over-zooming tightly clustered results.
    const zoom = map.getZoom();
    if (zoom && zoom > 15) {
      map.setZoom(15);
    }
  }, [
    map,
    locations,
    userLocation,
    emptyLocationsZoom,
    preferNearbyRadiusMiles,
    viewportInsetRef,
  ]);
};

interface PhlebotomyLocationFinderMapProps {
  locations: PhlebotomyLocation[];
  userLocation: { lat: number; lng: number } | null;
  selectedLocation: PhlebotomyLocation | null;
  onMarkerClick: (index: number) => void;
  onInfoWindowClose: () => void;
  defaultCenter?: { lat: number; lng: number };
  defaultZoom?: number;
  emptyLocationsZoom?: number;
  /**
   * When set, frame the camera to locations within this many miles when any
   * exist; otherwise frame to the nearest few. Markers still use `locations`.
   */
  preferNearbyRadiusMiles?: number;
  /** Portion of the map covered by the mobile results sheet. */
  bottomInsetRatio?: number;
  /** Fixed-height portion of the map covered by the mobile results sheet. */
  bottomInsetPixels?: number;
}
const PhlebotomyLocationFinderMap: React.FC<
  PhlebotomyLocationFinderMapProps
> = ({
  locations,
  userLocation,
  selectedLocation,
  onMarkerClick,
  onInfoWindowClose,
  defaultCenter = DEFAULT_MAP_CENTER,
  defaultZoom = DEFAULT_MAP_ZOOM,
  emptyLocationsZoom = 15,
  preferNearbyRadiusMiles,
  bottomInsetRatio = 0,
  bottomInsetPixels = 0,
}) => {
  const { t } = useTranslation();
  const map = useMap();
  const viewportInsetRef = useRef<MapViewportInset>({
    bottomInsetRatio,
    bottomInsetPixels,
  });
  viewportInsetRef.current = { bottomInsetRatio, bottomInsetPixels };
  useMapAutoCenter(
    map,
    locations,
    userLocation,
    emptyLocationsZoom,
    preferNearbyRadiusMiles,
    viewportInsetRef,
  );
  useEffect(() => {
    if (!map || !selectedLocation?.coordinates) return;
    const {
      bottomInsetRatio: currentBottomInsetRatio,
      bottomInsetPixels: currentBottomInsetPixels,
    } = viewportInsetRef.current;
    const selectionBottomInsetPixels =
      getBottomInsetPixels(
        map,
        currentBottomInsetRatio,
        currentBottomInsetPixels,
      ) / 2;
    map.panTo(
      getPaddedSelectionCenter(
        selectedLocation.coordinates,
        map.getZoom(),
        selectionBottomInsetPixels,
      ),
    );
  }, [map, selectedLocation]);

  const handleRecenter = () => {
    if (map && userLocation) {
      const viewportBottomInsetPixels = getBottomInsetPixels(
        map,
        bottomInsetRatio,
        bottomInsetPixels,
      );
      if (viewportBottomInsetPixels === 0) {
        map.panTo(userLocation);
        map.setZoom(14);
        return;
      }
      map.setZoom(14);
      map.panTo(
        getPaddedSelectionCenter(
          userLocation,
          14,
          viewportBottomInsetPixels / 2,
          0,
        ),
      );
    }
  };

  const handleMapClick = () => {
    if (selectedLocation) {
      onInfoWindowClose();
    }
  };

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      <Map
        defaultZoom={defaultZoom}
        defaultCenter={defaultCenter}
        mapId="bf404d538fuer23"
        disableDefaultUI
        zoomControl
        zoomControlOptions={{ position: ControlPosition.TOP_LEFT }}
        gestureHandling="greedy"
        clickableIcons={false}
        onClick={handleMapClick}
      >
        {userLocation && (
          <AdvancedMarker position={userLocation}>
            <div
              style={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                backgroundColor: "#4285F4",
                border: "2px solid #fff",
                boxShadow: "0 0 0 2px #4285F4",
              }}
            />
          </AdvancedMarker>
        )}
        {locations.map((location, index) =>
          location.coordinates ? (
            <AdvancedMarker
              key={`${location.name}-${location.address}`}
              position={location.coordinates}
              onClick={() => onMarkerClick(index)}
            >
              <Pin
                background={
                  selectedLocation === location ? "#DB4437" : "#FF7B6D"
                }
                borderColor={
                  selectedLocation === location ? "#B3261E" : "#E56254"
                }
              />
            </AdvancedMarker>
          ) : null,
        )}
        {selectedLocation?.coordinates && (
          <InfoWindow
            position={selectedLocation.coordinates}
            pixelOffset={[0, -40]}
            headerDisabled
            disableAutoPan
          >
            <Typography
              variant="h6"
              component="h3"
              sx={{
                fontWeight: "bold",
                fontSize: "18px",
                lineHeight: 1.2,
                mb: 0.5,
              }}
            >
              {selectedLocation.name}
            </Typography>
            <Box>
              <Typography variant="body2" sx={{ lineHeight: 1.25 }}>
                {selectedLocation.address}
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.25 }}>
                {`${selectedLocation.city}, ${selectedLocation.state} ${selectedLocation.zipCode}`}
              </Typography>
            </Box>
          </InfoWindow>
        )}
      </Map>
      {userLocation && (
        <Fab
          color="primary"
          aria-label={t("phlebotomyLocationFinder.map.recenterMap")}
          onClick={handleRecenter}
          sx={{ position: "absolute", top: 8, right: 16 }}
          size="small"
        >
          <NearMe />
        </Fab>
      )}
    </Box>
  );
};

export default PhlebotomyLocationFinderMap;
